// src/oura/lib/quoteText.ts
//
// Reads a signed ourteam quotation from its text: the quote number, who it
// was prepared for, the outlets and where they are, and which plans it
// offered. The PDF is what every client actually signs, and only a few
// clients have the quotation build's engagement.json beside it.
//
// The quotations have changed shape over time (a 2-part package in July, the
// proposal-and-quotation from September), so each fact has more than one way
// of being found, and a fact that is not found is left empty rather than
// guessed. A quote that offered both plans does not say which one was
// signed, so that is always asked.

export type QuotePlanKey = "annual" | "six" | "build";

export const QUOTE_PLANS: Record<QuotePlanKey, { planName: string; termMonths: number; label: string }> = {
    annual: { planName: "Annual plan", termMonths: 12, label: "Annual, 12 months" },
    // The name already says the term, so the label does not add it again ("6-Month, 6 months").
    six: { planName: "6-Month plan", termMonths: 6, label: "6-Month plan" },
    build: { planName: "Build only", termMonths: 1, label: "Build only, 1 month" },
};

export interface QuoteFromText {
    /** "OT-2026-LUSH-002". Null when the text carries no quote number. */
    ref: string | null;
    /** Who the quote was prepared for. Empty when not found. */
    client: string;
    /** Null when the quote does not say. */
    outlets: number | null;
    /** The outlet areas the quote names, in its order. */
    areas: string[];
    /** The plans the quote offers, in the order of QUOTE_PLANS. */
    plansOffered: QuotePlanKey[];
}

const REF = /\bOT-?(\d{4})-?([A-Z]{2,8}?)-?(\d{3})\b/;

// Lower case, or a capital first letter: "5 / 26 LOCATION QUESTIONS" is a
// heading, not 26 outlets.
const PLACE_WORDS = "[Cc]linics?|[Ss]tudios?|[Oo]utlets?|[Bb]ranch(?:es)?|[Cc]ent(?:re|er)s?|[Ll]ocations?|[Ss]pas?|[Ss]alons?|[Gg]yms?|[Ss]tores?|[Ss]hops?";
const NAMED_OUTLETS = new RegExp(`\\b(\\d{1,2})\\s+(?:${PLACE_WORDS})\\s*:\\s*([^\\n·]+)`);
const COUNTED_OUTLETS = new RegExp(`\\b(\\d{1,2})\\s+(?:${PLACE_WORDS})\\b`);

const LABELS = /^(reference|valid until|date|quotation no\.?|prepared ?by|replaces)\b/i;
/** Where a label runs into the name on the same line: "Biotropics Berhad Valid until 30 days". */
const TRAILING_LABEL = /\s+(?:valid until|date|reference|quotation no\.?|attn:|prepared ?by)\b.*$/i;

function normaliseRef(m: RegExpMatchArray): string {
    return `OT-${m[1]}-${m[2]}-${m[3]}`;
}

/** "BIOTROPICS MALAYSIA BERHAD" reads as a name; a mixed-case name is left alone. */
function titleIfShouting(s: string): string {
    if (s !== s.toUpperCase() || !/[A-Z]/.test(s)) return s;
    return s.toLowerCase().replace(/\b([a-z])/g, (c) => c.toUpperCase());
}

/** "P R E P A R E D F O R" is how a letter-spaced heading reads out of a PDF. */
function unspace(text: string): string {
    return text.replace(/\b[A-Z](?: [A-Z]){2,}\b/g, (m) => m.replace(/ /g, ""));
}

function findRef(text: string): string | null {
    // The labelled one first: a later quote also names the one it replaces.
    const labelled = text.match(new RegExp(`(?:reference|quotation no\\.?)[\\s:]*(${REF.source})`, "i"));
    const inLabel = labelled?.[1].match(REF);
    if (inLabel) return normaliseRef(inLabel);
    const any = text.match(REF);
    return any ? normaliseRef(any) : null;
}

function findClient(text: string): string {
    const lines = text.split("\n").map((l) => l.trim());
    for (let i = 0; i < lines.length; i++) {
        const m = lines[i].match(/^prepared ?for\b[\s:]*(.*)$/i);
        if (!m) continue;
        // "Prepared for The Lush Clinic", or the label on its own line with the name under it.
        let name = m[1].trim();
        for (let j = i + 1; !name && j < Math.min(lines.length, i + 4); j++) {
            if (lines[j] && !LABELS.test(lines[j])) name = lines[j];
        }
        name = name.split("·")[0].replace(TRAILING_LABEL, "").trim();
        const trading = name.match(/trading as\s+(.+)$/i);
        if (trading) name = trading[1].trim();
        name = name.replace(/[,.]$/, "").trim();
        if (name) return titleIfShouting(name);
    }
    return "";
}

function findOutlets(text: string): { outlets: number | null; areas: string[] } {
    const named = text.match(NAMED_OUTLETS);
    if (named) {
        const areas = named[2]
            .split(/,|\band\b/i)
            // A price set on the same line ("Sri Hartamas RM 200") is not part of the place.
            .map((s) => s.replace(/\s+(?:RM|MYR|USD)\b.*$/i, "").trim().replace(/[.;]$/, ""))
            .filter((s) => s.length > 0 && s.length < 40);
        return { outlets: Number(named[1]), areas };
    }
    const counted = text.match(COUNTED_OUTLETS);
    return { outlets: counted ? Number(counted[1]) : null, areas: [] };
}

function findPlans(text: string): QuotePlanKey[] {
    const found: QuotePlanKey[] = [];
    if (/\bannual\b|\b12 months\b/i.test(text)) found.push("annual");
    if (/\b6[- ]?month/i.test(text)) found.push("six");
    return found;
}

export function readQuoteText(text: string): QuoteFromText {
    const clean = unspace(text.replace(/\r/g, "").replace(/[ \t\u00a0]+/g, " "));
    const { outlets, areas } = findOutlets(clean);
    return { ref: findRef(clean), client: findClient(clean), outlets, areas, plansOffered: findPlans(clean) };
}
